/**
 * Help-center chat *conversation* shapes shared between the API (persistence, support-review
 * admin surface) and the web app (chat panel threading + feedback, the admin review UI).
 *
 * Distinct from `help-article.ts`, which covers the article corpus the chat answers from. Here we
 * model the transcripts themselves: every user question and grounded answer is persisted so the
 * support team can review them and improve the articles over time, and end users can rate answers.
 */

/** Author of a persisted chat turn. String values mirror the AI SDK message roles. */
export enum HelpChatRole {
	USER = "user",
	ASSISTANT = "assistant",
}

/** An end user's rating of an assistant answer — the signal support uses to find weak answers. */
export enum HelpChatFeedback {
	UP = "UP",
	DOWN = "DOWN",
}

/**
 * A help article the assistant actually opened while answering. Citations come from the article
 * record upstream, never from the model's prose, so the URL is safe to link and cannot be
 * hallucinated — but it points at the public help center, which is a different corpus from
 * anything this app stores. Persisted on the assistant turn as a snapshot, never a live FK.
 *
 * `title` is the article's own title in the language it was written in, which is not necessarily
 * the language of the answer beside it.
 */
export interface HelpArticleSource {
	slug: string;
	title: string;
	url: string;
}

/**
 * Where the user was in the app when they asked. Only the route *pattern* is captured (never a
 * resolved URL with record ids), so no tenant data lands in a transcript.
 */
export interface HelpMessagePageContext {
	routePattern?: string;
	label?: string;
	activeTab?: string;
}

/** One turn of a transcript as shown in the support-review admin surface. */
export interface HelpConversationMessageInfo {
	id: number;
	role: HelpChatRole;
	content: string;
	sources: HelpArticleSource[] | null;
	pageContext: HelpMessagePageContext | null;
	feedback: HelpChatFeedback | null;
	createdAt: string;
}

/** A conversation row in the support-review list (no message bodies — just the scan fields). */
export interface HelpConversationSummary {
	id: number;
	userId: number | null;
	companyId: number | null;
	language: string | null;
	/** Number of turns (user + assistant). */
	messageCount: number;
	/** First user question, for at-a-glance scanning of the list. */
	firstQuestion: string | null;
	/** Thumbs-up / thumbs-down tallies across the conversation's assistant answers. */
	upCount: number;
	downCount: number;
	createdAt: string;
	/** Timestamp of the most recent turn. */
	lastMessageAt: string;
}

/** A full transcript for the support-review detail view. */
export interface HelpConversationDetail extends HelpConversationSummary {
	messages: HelpConversationMessageInfo[];
}
